import type { ModelPickerOption } from '@developerz/domain';
import { IconChevronDown } from '@developerz/ui';
import { A } from '@solidjs/router';
import {
  createEffect,
  createMemo,
  createSignal,
  createUniqueId,
  For,
  type JSX,
  on,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { t } from '../i18n';
import { isListboxKey, nextListboxIndex } from '../lib/listbox-keys';
import { type PickerRow, pickerView, type SelectableRow } from '../lib/model-picker';
import { orgHref } from '../lib/org';
import { providerLabel } from './KeyCard';
import { ModelPickerRow } from './ModelPickerRow';

// The `ModelPicker`'s panel (#5210, epic #5200): a filter input with the ARIA
// combobox role over a listbox of the purpose's model options, grouped by provider —
// the pattern `CommandPaletteDialog.tsx` ships (`role="combobox"` input,
// `role="listbox"` results, `aria-activedescendant`), mounted inside the desktop
// `Popover` or the phone's bottom sheet.
//
// KEYS: ↑ ↓ walk the selectable rows (wrapping), Home and End jump to either end,
// Enter chooses, typing filters. Esc is deliberately NOT handled here: the Popover's
// (and the sheet's) own listener closes the panel and returns focus to the trigger,
// and it acts only when nothing inside called `preventDefault()` first.
//
// The rows, their order and which are selectable are `lib/model-picker.ts`'s; this
// file owns the cursor and the collapsed "Unavailable (n)" section.

export type KeysState = 'ready' | 'loading' | 'failed';

export type ModelPickerListProps = {
  readonly options: readonly ModelPickerOption[];
  readonly keysState: KeysState;
  /** Accessible name for the listbox and the input. */
  readonly label: string;
  readonly onChoose: (row: SelectableRow) => void;
};

export function ModelPickerList(props: ModelPickerListProps): JSX.Element {
  const listId = createUniqueId();
  const [query, setQuery] = createSignal('');
  const [showUnavailable, setShowUnavailable] = createSignal(false);
  const view = createMemo(() => pickerView(props.options, query(), t('agents.automatic')));
  const [cursor, setCursor] = createSignal(view().initial);
  let input: HTMLInputElement | undefined;

  // Typing re-aims the cursor at the first match; the rows under the old index are
  // unrelated to the new query. Clamped, so a shrinking list never selects nothing.
  createEffect(on(query, () => setCursor(0), { defer: true }));
  const active = (): number => Math.min(cursor(), Math.max(view().selectable.length - 1, 0));
  const selected = (): SelectableRow | undefined => view().selectable[active()];
  const domId = (row: PickerRow): string => `${listId}-${row.id.replace(/[^\w-]/g, '_')}`;

  // Focus the input once the panel is on screen. A macrotask, not a microtask: the
  // sheet's dialog trap focuses its first control in a microtask, and the filter —
  // not the sheet's close button — is where the keyboard contract starts.
  onMount(() => {
    const timer = setTimeout(() => input?.focus({ preventScroll: true }), 0);
    onCleanup(() => clearTimeout(timer));
  });

  createEffect(() => {
    const row = selected();
    if (row === undefined) return;
    document.getElementById(domId(row))?.scrollIntoView?.({ block: 'nearest' });
  });

  const onKeyDown = (event: KeyboardEvent): void => {
    const total = view().selectable.length;
    if (isListboxKey(event.key)) {
      event.preventDefault();
      if (total > 0) setCursor(nextListboxIndex(active(), total, event.key));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const row = selected();
      if (row !== undefined) props.onChoose(row);
    }
  };

  const pickable = (row: PickerRow): row is SelectableRow =>
    row.kind === 'automatic' || row.kind === 'model';
  const option = (row: PickerRow): JSX.Element => (
    <ModelPickerRow
      row={row}
      domId={domId(row)}
      active={selected()?.id === row.id}
      {...(pickable(row) ? { onPick: () => props.onChoose(row) } : {})}
    />
  );

  return (
    <div class="model-picker__panel">
      <input
        ref={input}
        class="model-picker__filter"
        type="text"
        role="combobox"
        aria-expanded={true}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          selected() === undefined ? undefined : domId(selected() as PickerRow)
        }
        aria-label={t('agents.picker.filter')}
        placeholder={t('agents.picker.filter')}
        value={query()}
        onInput={(e) => setQuery(e.currentTarget.value)}
        onKeyDown={onKeyDown}
      />
      <div class="model-picker__list" id={listId} role="listbox" aria-label={props.label}>
        <For each={view().head}>{option}</For>
        <For each={view().groups}>
          {(group) => (
            // biome-ignore lint/a11y/useSemanticElements: `group` is the ARIA role a listbox may own between itself and its options; a <fieldset> would break `aria-required-parent` (the palette's own ruling, CommandPaletteGroup.tsx).
            <div
              class="model-picker__group"
              role="group"
              aria-label={providerLabel(group.provider)}
            >
              <div class="model-picker__group-label" aria-hidden="true">
                {providerLabel(group.provider)}
              </div>
              <For each={group.rows}>{option}</For>
            </div>
          )}
        </For>
        <Show when={showUnavailable() && view().unavailable.length > 0}>
          {/* biome-ignore lint/a11y/useSemanticElements: as above — a listbox-owned group, not a form fieldset. */}
          <div
            class="model-picker__group"
            role="group"
            aria-label={t('agents.picker.unavailable', { n: view().unavailable.length })}
          >
            <For each={view().unavailable}>{option}</For>
          </div>
        </Show>
      </div>

      <Show when={view().noMatch}>
        <p class="model-picker__note">{t('agents.picker.no_match')}</p>
      </Show>
      <Show when={props.keysState !== 'ready'}>
        <p class="model-picker__note">
          {props.keysState === 'loading'
            ? t('agents.picker.keys_loading')
            : t('agents.picker.keys_failed')}
        </p>
      </Show>
      <Show when={props.keysState === 'ready' && view().empty}>
        <p class="model-picker__note">
          <A href={orgHref('/account/keys')}>{t('agents.picker.empty')}</A>
        </p>
      </Show>
      <Show when={view().unavailable.length > 0}>
        <div class="model-picker__unavailable">
          <button
            type="button"
            class="model-picker__toggle"
            aria-expanded={showUnavailable()}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setShowUnavailable((v) => !v)}
          >
            <IconChevronDown class="model-picker__chevron" size="0.875rem" />
            {t('agents.picker.unavailable', { n: view().unavailable.length })}
          </button>
          <A href={orgHref('/account/keys')} class="model-picker__fix">
            {t('agents.picker.unavailable_fix')}
          </A>
        </div>
      </Show>
    </div>
  );
}
